import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Box,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { scheduledEventRequests } from 'redux/requests/scheduled-event.requests';
import { useScheduledEventSelectors } from 'redux/slices/scheduledEventSlice';
import { useApiCall } from 'redux/utils/api-hook';
import styled from 'styled-components';
import { HighlightableRow } from 'toolkit/tables/highlightable-row';
import { StatusTag } from 'toolkit/tags/status-tags';
import { timeFormatter } from '@act/shared/utilities';
import { useNavigateToMemberPage } from '../../app/routing/navigation-hooks';
import useInteractionsDialog from '../../dialog-forms/hooks/use-interactions-dialog';
import { PendingItemWithMember } from './utilities/expandedPendingItem.type';
import { displayName } from './utilities/displayNameAndBirthdayCheck.utils';

interface PendingRowProps {
  data?: PendingItemWithMember;
  showDate: boolean;
}

const eventTypeToLabel = {
  call: 'Call',
  message: 'Message',
  visit: 'Visit',
  workflow_phase: 'Workflow',
  transfer_request: 'Transfer request',
  status_change_request: 'Status change request',
};

const DivFitContent = styled.div`
  width: fit-content;
`;

const PendingItemTableRow = ({ data, showDate }: PendingRowProps) => {
  const toMemberPage = useNavigateToMemberPage();
  const interactionsDialogHelper = useInteractionsDialog();
  const scheduledEvent = useScheduledEventSelectors.useSelectScheduledEventById(
    data?.externalItemId
  );
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  let menuItems;
  const [_, __, deleteScheduledEvent] = useApiCall(
    scheduledEventRequests.deleteScheduledEventForUser
  );
  if (
    !data ||
    (data?.externalItemType === 'scheduled_event' && !scheduledEvent)
  ) {
    // data not fully loaded (or being unloaded)
    return <TableRow></TableRow>;
  }
  if (data?.externalItemType === 'scheduled_event') {
    menuItems = [];
    switch (scheduledEvent.type) {
      case 'visit':
        menuItems.push({
          display: 'Record visit now',
          callback: () =>
            interactionsDialogHelper.createVisit({
              userId: scheduledEvent.userId,
              scheduledEvent,
            }),
        });
        break;
      case 'call':
        menuItems.push({
          display: 'Call now',
          callback: () =>
            interactionsDialogHelper.createCall({
              scheduledEvent,
              user: data.member,
            }),
        });
        break;
      case 'message':
        menuItems.push({
          display: 'Message now',
          callback: () =>
            interactionsDialogHelper.createMessage({
              scheduledEventId: scheduledEvent.id,
              user: data.member,
            }),
        });
    }
    menuItems.push({
      display: 'Reschedule event',
      callback: () =>
        interactionsDialogHelper.editScheduledEvent({
          scheduledEvent,
          user: data.member,
        }),
    });
    menuItems.push({
      display: 'Delete event',
      callback: () =>
        deleteScheduledEvent({
          params: {
            userId: data.memberId,
            scheduledEventId: scheduledEvent.id,
          },
        }),
    });
  } else {
    menuItems = [
      {
        display: 'View request',
        callback: () =>
          toMemberPage(data.member.assignedGuideId, data.member.id),
      },
    ];
  }

  const eventName: string = eventTypeToLabel[data.eventType];
  let timeString: string;
  if (showDate) {
    timeString = DateTime.fromMillis(data.timestamp).toFormat('LLL dd, h:mma');
    if (data.externalItemType === 'scheduled_event' && !scheduledEvent.time) {
      timeString = DateTime.fromMillis(data.timestamp).toFormat('LLL dd, ––');
    }
  } else {
    timeString = timeFormatter.epochToHumanReadableTime(data.timestamp);
    if (data.externalItemType === 'scheduled_event' && !scheduledEvent.time) {
      timeString = null;
    }
  }

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  return (
    <>
      <HighlightableRow
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          toMemberPage(data.member.assignedGuideId, data.member.id);
        }}
      >
        <TableCell>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName(data?.member)}
          </Box>
        </TableCell>
        <TableCell>
          <DivFitContent>
            <StatusTag status={data.member?.memberStatus} />
          </DivFitContent>
        </TableCell>
        <TableCell>{eventName}</TableCell>
        <TableCell>{timeString || '––'}</TableCell>
        <TableCell>
          <IconButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleOpenMenu(e);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </TableCell>
        <TableCell>
          <IconButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              toMemberPage(data.member.assignedGuideId, data.member.id);
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </TableCell>
      </HighlightableRow>
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuItems.map(item => (
          <MenuItem
            key={item.display}
            onClick={() => {
              item.callback(data);
              handleCloseMenu();
            }}
          >
            {item.display}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default PendingItemTableRow;
